import { isPast } from 'date-fns'
import { isFunction } from 'lodash-es'

import { CustomerSubpopulations } from '../../../models/customer/Customer'
import { CustomerPropertiesMap } from '../../../models/user/User'
import { isTreatmentOptionCoaching } from '../../searchForCare/utils'
import { excludedServices as defaultExcludedServices } from '../constants/constants'
import { CustomerInstanceMap, CustomerInstanceRedirectionProps } from '../constants/customer'
import { REGISTRATION_EVENTS } from '../constants/mixpanelConstants'
import { TREATMENT_OPTIONS_TYPE } from '../constants/treatmentOptions'

interface CustomerInfo extends CustomerPropertiesMap {
  eligibilityType?: string
}

export const generateServicesNotCovered = ({ excludedServices = defaultExcludedServices }) => {
  return `Services that are not covered include: ${excludedServices}.
      Please check with your health plan or benefits portal for coverage of these excluded services.`
}

// Passes an object of customer properties to a customer copy, if applicable
export const generateCustomerCopyWithProperties = ({
  customerName,
  copy,
  customerInfo,
  customerDisplayName,
  isCustomerDynamicSessionLimitEnabled,
  isDBTTeensEnabled,
}: {
  customerName: string
  copy: any
  customerInfo: CustomerInfo
  customerDisplayName: string
  isCustomerDynamicSessionLimitEnabled?: boolean
  isDBTTeensEnabled?: boolean
}) => {
  return isFunction(copy)
    ? copy({
        ...customerInfo,
        customerName,
        customerDisplayName,
        isCustomerDynamicSessionLimitEnabled,
        isDBTTeensEnabled,
      })
    : copy
}

export const getEligibleInstances = (customerInstanceMap: CustomerInstanceMap, customerName: string): string[] => {
  if (
    !customerInstanceMap ||
    !Object.prototype.hasOwnProperty.call(customerInstanceMap, customerName) ||
    customerInstanceMap[customerName].instances.length === 0
  ) {
    return []
  }

  const allInstances = [customerName, ...customerInstanceMap[customerName].instances]
  return allInstances.filter((instance) => {
    if (!customerInstanceMap[instance]) {
      return false
    }
    if (Object.prototype.hasOwnProperty.call(customerInstanceMap[instance], 'launchDate')) {
      const date = new Date(customerInstanceMap[instance].launchDate as string)
      if (isNaN(date.getTime())) {
        return false
      }
      return isPast(date)
    }
    return true
  })
}

export const showSubpopulationModal = (customerInstanceMap: CustomerInstanceMap, customerName: string) => {
  return getEligibleInstances(customerInstanceMap, customerName).length >= 2
}

export const getFilteredSubpopulations = (
  customerInstanceMap: CustomerInstanceMap,
  customerName: string,
  subpopulations: CustomerSubpopulations,
): { subpopulations: CustomerSubpopulations; eligibleCustomerNames: string[] } => {
  const eligibleInstances = getEligibleInstances(customerInstanceMap, customerName)
  const eligibleSet = new Set(eligibleInstances)
  const allInstances = [customerName, ...(customerInstanceMap[customerName]?.instances ?? [])]

  const filteredOptions = subpopulations.options.filter((_, index) => {
    const instanceName = allInstances[index]
    return instanceName !== undefined && eligibleSet.has(instanceName)
  })

  return {
    subpopulations: { ...subpopulations, options: filteredOptions },
    eligibleCustomerNames: eligibleInstances,
  }
}

export const buildCustomerInstanceRedirectionProps = ({
  customerInstanceMap,
  customerName,
  navigate,
  trackEvent,
}: CustomerInstanceRedirectionProps) => {
  const handleRedirect = (newCustomerName: string) => {
    trackEvent(REGISTRATION_EVENTS.CLICK_REDIRECTION_LINK, { customerName, newCustomerName })
    navigate(newCustomerName)
  }
  if (
    !!customerInstanceMap &&
    Object.prototype.hasOwnProperty.call(customerInstanceMap, customerName) &&
    customerInstanceMap[customerName].instances.length > 0
  ) {
    return handleRedirect(customerInstanceMap[customerName].instances[0])
  }
}

export const getSubpopulationCustomerName = (
  customerInstanceMap: CustomerInstanceMap,
  customerName: string,
  newCustomerIndex: number,
) => {
  let newCustomerName = customerName
  // The customerInstanceMap LD flag contains a mapping of multi-instance customers to their other instances' customer names. Currently only supports 1 alternative instance per customer.
  // To retrieve the customer names based on index, it is designated that:
  // newCustomerIndex = 0 corresponds to the current customer
  // newCustomerIndex = 1 corresponds to customerInstanceMap[currentCustomer].instances[0]
  // newCustomerIndex = x correponds to customerInstanceMap[currentCustomer].instances[x - 1]
  if (newCustomerIndex - 1 >= 0) {
    if (
      !!customerInstanceMap &&
      Object.prototype.hasOwnProperty.call(customerInstanceMap, customerName) &&
      customerInstanceMap[customerName].instances.length > 0
    ) {
      newCustomerName = customerInstanceMap[customerName].instances[newCustomerIndex - 1]
    }
  }
  return newCustomerName
}

export const isCustomerSoleraCoaching = (customerName: string | undefined): boolean => {
  return customerName?.toLowerCase().startsWith('enrollment') ?? false
}

const SOLERA_COACHING_THERAPY_MEDS_CUSTOMER_REGEX =
  /^enroll-plus(-(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec))?$/i

/**
 * Solera customers whose contract covers coaching, therapy and meds (enroll-plus, enroll-plus-jan ... enroll-plus-dec).
 */
export const isCustomerSoleraCoachingTherapyMeds = (customerName: string | undefined): boolean => {
  return SOLERA_COACHING_THERAPY_MEDS_CUSTOMER_REGEX.test(customerName ?? '')
}

/**
 * For SoleraCoachingTherapyMeds customers, the cost estimate tile should be hidden for therapy and meds providers /
 * booking flows (client may be charged for cost sharing), but still shown for coaching (fully covered).
 *
 * When there is no selected treatment option (e.g. direct-link booking), default to hiding.
 */
export const shouldHideCostEstimateForSoleraCoachingTherapyMeds = ({
  customerName,
  selectedTreatmentOption,
}: {
  customerName: string | undefined
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null
}): boolean =>
  isCustomerSoleraCoachingTherapyMeds(customerName) && !isTreatmentOptionCoaching(selectedTreatmentOption ?? null)
